import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useToast } from "../contexts/ToastContext";

const PERM_OPTIONS = [
  { value: "lecture",    label: "Lecture seule",          desc: "Consultation uniquement" },
  { value: "ecriture",   label: "Lecture + Écriture",     desc: "Peut saisir des écritures" },
  { value: "validation", label: "Validation",             desc: "Peut valider les écritures" },
  { value: "admin",      label: "Accès complet",          desc: "Tous les droits" },
];

const SCOPE_LABELS = {
  all:      "Accès complet",
  invoices: "Facturation",
  expenses: "Notes de frais",
  reports:  "Rapports",
};

// Statut de dossier — organisationnel, piloté par le cabinet depuis sa
// propre session (server/routes/expertAccess.js). Lecture seule ici :
// le client voit où en est son dossier mais ne le modifie pas depuis cet
// écran. Sans lien avec companies.status.
const DOSSIER_STATUS_LABELS = {
  a_traiter: { label: "À traiter", bg: "#fef3c7", color: "#92400e" },
  en_cours:  { label: "En cours",  bg: "#dbeafe", color: "#1e40af" },
  cloture:   { label: "Clôturé",   bg: "#dcfce7", color: "#166534" },
};

export default function ExpertComptable({ currentCompanyId }) {
  const toast = useToast();
  const [accesses, setAccesses]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [inviteLink, setInviteLink] = useState(null);  // modale lien
  const [form, setForm] = useState({
    expert_name: "", expert_email: "", cabinet: "",
    permission_level: "lecture", scope: "all", expires_days: "365",
  });
  const [saving, setSaving]   = useState(false);
  const [copied, setCopied]   = useState(false);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.externalAccess.list(currentCompanyId)
      .then(res => setAccesses(Array.isArray(res) ? res : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.externalAccess.create({
        company_id:       currentCompanyId,
        expert_name:      form.expert_name,
        expert_email:     form.expert_email,
        cabinet:          form.cabinet,
        permission_level: form.permission_level,
        expires_days:     parseInt(form.expires_days) || 365,
      });
      setShowModal(false);
      setForm({ expert_name: "", expert_email: "", cabinet: "", permission_level: "lecture", scope: "all", expires_days: "365" });
      // Afficher le lien dans une modale dédiée
      const link = res.invite_link || `${window.location.origin}/invite?token=${res.token}`;
      setInviteLink({ name: res.expert_name || res.name, link });
      load();
      toast.success(`Invitation créée pour ${res.expert_name || form.expert_name}`);
      console.log(`[Expert invite] Lien d'invitation : ${link}`);
    } catch (err) {
      toast.error(err.message || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (id, name) => {
    await api.externalAccess.revoke(id).catch(e => toast.error(e.message));
    toast.success(`Accès de ${name} révoqué`);
    load();
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true);
      toast.success("Lien copié dans le presse-papier");
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => toast.error("Impossible de copier"));
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  const active  = accesses.filter(a => a.is_active);
  const expired = accesses.filter(a => !a.is_active);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Expert-comptable & accès externes</h2>
        <button className="btn primary" onClick={() => setShowModal(true)}>🔗 Générer un lien d'invitation</button>
      </div>

      <div className="kpi-grid k3">
        <div className="kpi"><div className="kpi-label">Accès actifs</div><div className="kpi-value" style={{ color: "var(--g)" }}>{active.length}</div></div>
        <div className="kpi"><div className="kpi-label">Expirés / Révoqués</div><div className="kpi-value" style={{ color: "var(--text-muted)" }}>{expired.length}</div></div>
        <div className="kpi"><div className="kpi-label">Total invitations</div><div className="kpi-value">{accesses.length}</div></div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">🔑 Accès actifs</span></div>
        {active.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "12px 0" }}>
            Aucun accès externe actif. Cliquez sur "Générer un lien d'invitation" pour inviter votre expert-comptable.
          </div>
        ) : active.map(a => {
          const exp = a.expires_at ? new Date(a.expires_at) : null;
          const daysLeft = exp ? Math.ceil((exp - Date.now()) / 86400000) : null;
          const invLink = `${window.location.origin}/invite?token=${a.token?.replace('...', '')}`;
          return (
            <div key={a.id} style={{ padding: "12px 0", borderBottom: "0.5px solid #f3f4f6", display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div className="avatar" style={{ width: 36, height: 36, flexShrink: 0 }}>
                {(a.expert_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{a.expert_name}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{a.expert_email}{a.cabinet ? ` · ${a.cabinet}` : ""}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, background: "#e6f1fb", color: "#185fa5", fontWeight: 700 }}>
                    {PERM_OPTIONS.find(p => p.value === a.permission_level)?.label || a.permission_level}
                  </span>
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, background: "#f3f4f6", fontWeight: 600 }}>{SCOPE_LABELS[a.scope] || "Accès complet"}</span>
                  {a.dossier_status && (
                    <span
                      title="Statut de dossier défini par le cabinet"
                      style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 5, fontWeight: 700,
                        background: DOSSIER_STATUS_LABELS[a.dossier_status]?.bg || "#f3f4f6",
                        color: DOSSIER_STATUS_LABELS[a.dossier_status]?.color || "var(--text-muted)",
                      }}
                    >
                      📁 {DOSSIER_STATUS_LABELS[a.dossier_status]?.label || a.dossier_status}
                    </span>
                  )}
                  {daysLeft !== null && (
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, background: daysLeft < 7 ? "var(--red-light)" : "var(--gl)", color: daysLeft < 7 ? "var(--red)" : "var(--gm)", fontWeight: 600 }}>
                      {daysLeft > 0 ? `Expire dans ${daysLeft}j` : "Expiré"}
                    </span>
                  )}
                  {a.last_access && <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Dernière connexion : {new Date(a.last_access).toLocaleDateString("fr-FR")}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                <button className="btn sm" onClick={() => setInviteLink({ name: a.expert_name, link: invLink })} title="Voir le lien">🔗</button>
                <button className="btn sm" style={{ color: "var(--red)" }} onClick={() => handleRevoke(a.id, a.expert_name)}>🚫 Révoquer</button>
              </div>
            </div>
          );
        })}
      </div>

      {expired.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-header"><span className="card-title" style={{ color: "var(--text-muted)" }}>🗂 Historique des accès révoqués/expirés</span></div>
          {expired.map(a => (
            <div key={a.id} style={{ padding: "8px 0", borderBottom: "0.5px solid #f9fafb", display: "flex", alignItems: "center", gap: 10, opacity: 0.6 }}>
              <div className="avatar" style={{ width: 28, height: 28, fontSize: 10, flexShrink: 0 }}>{(a.expert_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{a.expert_name}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.expert_email}</div>
              </div>
              <span className="badge danger" style={{ fontSize: 9 }}>Révoqué</span>
            </div>
          ))}
        </div>
      )}

      {/* Modale création invitation */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">🧑‍💼 Inviter un expert-comptable</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Nom complet *</label>
                <input className="form-control" required placeholder="Jean Dupont" value={form.expert_name} onChange={e => setForm(f => ({ ...f, expert_name: e.target.value }))} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="form-control" type="email" required value={form.expert_email} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, expert_email: v })); }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Cabinet</label>
                  <input className="form-control" placeholder="Cabinet Dupont & Associés" value={form.cabinet} onChange={e => setForm(f => ({ ...f, cabinet: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Niveau d'accès</label>
                  <select className="form-control" value={form.permission_level} onChange={e => setForm(f => ({ ...f, permission_level: e.target.value }))}>
                    {PERM_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label} — {p.desc}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Durée de validité</label>
                  <select className="form-control" value={form.expires_days} onChange={e => setForm(f => ({ ...f, expires_days: e.target.value }))}>
                    <option value="7">7 jours</option>
                    <option value="30">30 jours</option>
                    <option value="90">3 mois</option>
                    <option value="365">1 an</option>
                    <option value="3650">Permanent</option>
                  </select>
                </div>
              </div>
              <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "Génération…" : "🔗 Générer le lien"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale affichage lien d'invitation */}
      {inviteLink && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setInviteLink(null)}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3 className="modal-title">🔗 Lien d'invitation — {inviteLink.name}</h3>
              <button className="modal-close" onClick={() => setInviteLink(null)}>✕</button>
            </div>
            <div style={{ padding: "12px 0" }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
                Partagez ce lien avec <strong>{inviteLink.name}</strong>. Il lui donnera accès à votre espace selon les permissions définies.
              </div>
              <div style={{ background: "#f9fafb", border: "var(--border)", borderRadius: 8, padding: "12px 14px", fontFamily: "monospace", fontSize: 12, wordBreak: "break-all", color: "var(--text)", marginBottom: 14 }}>
                {inviteLink.link}
              </div>
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#92400e", marginBottom: 16 }}>
                ⚠️ Ce lien est à usage unique et confidentiel. Ne le partagez qu'avec votre expert-comptable.
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn" onClick={() => setInviteLink(null)}>Fermer</button>
                <button className="btn primary" onClick={() => copyLink(inviteLink.link)}>
                  {copied ? "✅ Copié !" : "📋 Copier le lien"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
